import { createContext, useContext } from "react";
import { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import { ReadQuestionnaire, WriteQuestionnaire } from "@/types/questionnaire";
import { ReadDisabilityCertificate } from "@/types/disabilityCertificate";
import { SuccessFetchListResponse, ApiError } from "@/services/api";
import type { PresignedClient } from "@/lib/file-storage/types";

export type QuestionnaireStep = "form" | "summary" | "final";

export type QuestionnaireHandlerKey = "submit" | "accept" | "reject";

export type QuestionnaireAlertReason =
  | "invalid_token"
  | "query_error"
  | "no_questionnaire"
  | "no_agreement"
  | null;

export type QuestionnaireLoadingReason =
  | "loading_data"
  | "submitting_form"
  | "accepting_agreement"
  | "rejecting_agreement"
  | "unknown";

export type QuestionnaireLoadingState =
  | { isLoading: false }
  | { isLoading: true; reason: QuestionnaireLoadingReason };

export interface MutationsObject {
  submitQuestionnaireMutation: UseMutationResult<
    { pdfData: string },
    ApiError,
    WriteQuestionnaire,
    unknown
  >;
  acceptAgreementMutation: UseMutationResult<
    undefined,
    ApiError,
    undefined,
    unknown
  >;
  cancelAgreementMutation: UseMutationResult<
    undefined,
    ApiError,
    undefined,
    unknown
  >;
  submitStatementsMutation: UseMutationResult<
    undefined,
    ApiError,
    { rodo_accepted: boolean; regulations_accepted: boolean },
    unknown
  >;
}
export interface QueriesObject {
  questionnaireQuery: UseQueryResult<ReadQuestionnaire, Error>;
  disabilityCertificateQuery?: UseQueryResult<
    SuccessFetchListResponse<ReadDisabilityCertificate>,
    Error
  >;
}

export interface UseQuestionnaireHandlersProps {
  agreementId?: string;
  token: string;
  agreementStatus?: string | null;
  setCurrentStep: (step: "form" | "summary" | "final") => void;
  setIsAccepted: (accepted: boolean) => void;
  setSummaryPdfBase64: (pdf: string | null) => void;
  openDialog: (
    action: QuestionnaireHandlerKey,
    onConfirm: () => void,
    warningDescription?: string | null,
  ) => void;
  closeDialog: () => void;
  setPersistedFormData: (data: WriteQuestionnaire | null) => void;
  questionnaire?: ReadQuestionnaire["questionnaire"];
}
interface QuestionnaireContextType {
  token: string;
  questionnaireQuery: UseQueryResult<ReadQuestionnaire, Error>;
  disabilityCertificateQuery: UseQueryResult<
    SuccessFetchListResponse<ReadDisabilityCertificate>,
    Error
  >;
  presignedClient: PresignedClient;

  currentStep: QuestionnaireStep;
  isAccepted: boolean;
  summaryPdfBase64: string | null;
  persistedFormData: WriteQuestionnaire | null;

  loadingState: QuestionnaireLoadingState;
  errorState: { error: Error | null; isQuery: boolean };
  shouldShowAlert: boolean;
  alertReason: QuestionnaireAlertReason;

  handleSubmitForm: (
    data: WriteQuestionnaire,
    warningDescription?: string | null,
  ) => Promise<void>;
  handleSubmitFormWithoutConfirm: (data: WriteQuestionnaire) => Promise<void>;
  handleAcceptAgreement: () => Promise<void>;
  handleCancelAgreement: () => Promise<void>;
  clearError: () => void;
  refreshQuestionnaire: () => void;
}

const QuestionnaireContext = createContext<QuestionnaireContextType>(
  {} as QuestionnaireContextType,
);

export const useQuestionnaireContext = () => {
  const context = useContext(QuestionnaireContext);
  if (!context) {
    throw new Error(
      "useQuestionnaireContext must be used within QuestionnaireProvider",
    );
  }
  return context;
};

export { QuestionnaireContext };
export type { QuestionnaireContextType };
export { QuestionnaireProvider } from "./QuestionnaireProvider";
